import { useCallback, useEffect, useRef, useState } from 'react'
import { CrmApiError } from '../http'
import { createCapture, patchCapture } from './api'
import { AUTOSAVE_MS, MIN_CHARS } from './constants'
import {
  loadLocalDraft,
  newestLocalDraft,
  pruneLocalDrafts,
  rememberNoticeOnDevice,
  removeLocalDraft,
  saveLocalDraft,
} from './storage'
import type { CaptureContext, InputMethod } from './types'

export type DraftState = 'idle' | 'pending' | 'saving' | 'saved' | 'offline' | 'error'

export interface DraftInitial {
  /** The server id of an existing capture, or null when only a local copy is known. */
  id: string | null
  clientCaptureId: string
  text: string
  inputMethod: InputMethod | null
}

interface Options {
  noticeVersion: number
  context: CaptureContext | null
  initial?: DraftInitial
}

export interface OfflineDraft {
  text: string
  setText: (text: string) => void
  inputMethod: InputMethod
  setInputMethod: (method: InputMethod) => void
  /** The server id once the concept exists. */
  captureId: string | null
  state: DraftState
  /** The text came back from a local copy (a reload, a closed tab). */
  recovered: boolean
  /** Saves now (skipping the 2-second wait) and resolves to the server id, or null when the latest text could not be saved. */
  flush: () => Promise<string | null>
}

const sameContext = (a: CaptureContext | null, b: CaptureContext | null) =>
  a?.type === b?.type && a?.id === b?.id

interface Start {
  text: string
  clientId: string | null
  serverId: string | null
  method: InputMethod
  recovered: boolean
}

function startFrom({ context, initial }: Options): Start {
  pruneLocalDrafts()
  if (initial) {
    const local = loadLocalDraft(initial.clientCaptureId)
    return {
      text: local?.text ?? initial.text,
      clientId: initial.clientCaptureId,
      serverId: initial.id,
      method: local?.inputMethod ?? initial.inputMethod ?? 'toetsenbord',
      recovered: local !== null,
    }
  }
  const newest = newestLocalDraft()
  if (newest && (context === null || sameContext(newest.draft.context, context))) {
    return {
      text: newest.draft.text,
      clientId: newest.clientCaptureId,
      serverId: null,
      method: newest.draft.inputMethod ?? 'toetsenbord',
      recovered: true,
    }
  }
  return {
    text: '',
    clientId: null,
    serverId: null,
    method: 'toetsenbord',
    recovered: false,
  }
}

/**
 * The text of a memo and its safety net (ED-75). From 20 characters on a concept exists on
 * the server (idempotent on `client_capture_id`); it autosaves 2 s after the last change.
 * When saving fails (offline, time-out) the text is kept in `localStorage`, tried again on
 * `online`, and removed after a successful save. A copy older than 7 days is dropped.
 */
export function useOfflineDraft(options: Options): OfflineDraft {
  const { noticeVersion, context } = options
  const [start] = useState(() => startFrom(options))
  const [text, setTextState] = useState(start.text)
  const [inputMethod, setMethodState] = useState<InputMethod>(start.method)
  const [captureId, setCaptureId] = useState<string | null>(start.serverId)
  const [state, setState] = useState<DraftState>('idle')

  const textRef = useRef(start.text)
  const methodRef = useRef(start.method)
  const clientIdRef = useRef(start.clientId)
  const serverIdRef = useRef(start.serverId)
  const versionRef = useRef(start.recovered ? 1 : 0)
  const savedVersionRef = useRef(0)
  const stateRef = useRef<DraftState>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const chainRef = useRef<Promise<string | null> | null>(null)
  const contextRef = useRef(context)
  // The context the server knows about (set after create/patch; initial = what the page opened with).
  const sentContextRef = useRef<CaptureContext | null>(context)
  const noticeRef = useRef(noticeVersion)
  useEffect(() => {
    contextRef.current = context
    noticeRef.current = noticeVersion
  })

  const report = useCallback((next: DraftState) => {
    stateRef.current = next
    setState(next)
  }, [])

  const ensureClientId = useCallback((): string => {
    clientIdRef.current ??= crypto.randomUUID()
    return clientIdRef.current
  }, [])

  const run = useCallback(async (): Promise<string | null> => {
    for (;;) {
      const version = versionRef.current
      const body = {
        transcript: textRef.current,
        input_method: methodRef.current,
      }
      const contextChanged = !sameContext(sentContextRef.current, contextRef.current)
      if (version === savedVersionRef.current && serverIdRef.current && !contextChanged) {
        report('saved')
        return serverIdRef.current
      }
      if (!serverIdRef.current && body.transcript.trim().length < MIN_CHARS) {
        report('idle')
        return null
      }
      report('saving')
      const clientId = ensureClientId()
      try {
        if (serverIdRef.current) {
          await patchCapture(serverIdRef.current, {
            ...body,
            ...(contextChanged ? { context: contextRef.current } : {}),
          })
          sentContextRef.current = contextRef.current
        } else {
          const created = await createCapture({
            client_capture_id: clientId,
            ...body,
            context: contextRef.current,
            notice_version: noticeRef.current,
          })
          serverIdRef.current = created.id
          setCaptureId(created.id)
          sentContextRef.current = contextRef.current
          // Creating is idempotent: a retry after a lost response gets the OLD row back,
          // possibly with older text. Bring it up to date before calling the text saved.
          if (created.transcript !== body.transcript || created.input_method !== body.input_method) {
            await patchCapture(created.id, {
              ...body,
              context: contextRef.current,
            })
          }
          rememberNoticeOnDevice(noticeRef.current)
        }
        savedVersionRef.current = version
        removeLocalDraft(clientId)
        if (versionRef.current !== version) continue
        report('saved')
        return serverIdRef.current
      } catch (caught) {
        saveLocalDraft(clientId, {
          text: body.transcript,
          savedAt: Date.now(),
          context: contextRef.current,
          inputMethod: body.input_method,
        })
        const lost = !navigator.onLine || (caught instanceof CrmApiError && caught.code === 'network')
        report(lost ? 'offline' : 'error')
        // The latest text is not on the server: never hand back an id that points at stale text.
        return null
      }
    }
  }, [ensureClientId, report])

  const save = useCallback((): Promise<string | null> => {
    chainRef.current ??= run().finally(() => {
      chainRef.current = null
    })
    return chainRef.current
  }, [run])

  const schedule = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      void save()
    }, AUTOSAVE_MS)
  }, [save])

  // Removing (or changing) the context after the concept exists must reach the server too.
  useEffect(() => {
    if (serverIdRef.current && !sameContext(sentContextRef.current, context)) {
      versionRef.current += 1
      stateRef.current = 'pending'
      setState('pending')
      schedule()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context])

  const touched = useCallback(() => {
    versionRef.current += 1
    if (serverIdRef.current || textRef.current.trim().length >= MIN_CHARS) {
      report('pending')
      schedule()
    } else {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = null
      report('idle')
    }
  }, [report, schedule])

  const setText = useCallback(
    (next: string) => {
      if (next === textRef.current) return
      textRef.current = next
      setTextState(next)
      touched()
    },
    [touched],
  )

  const setInputMethod = useCallback(
    (method: InputMethod) => {
      if (method === methodRef.current) return
      methodRef.current = method
      setMethodState(method)
      touched()
    },
    [touched],
  )

  const flush = useCallback((): Promise<string | null> => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
    return save()
  }, [save])

  // A recovered copy goes to the server shortly after the page opens.
  useEffect(() => {
    if (versionRef.current > savedVersionRef.current && textRef.current.trim().length >= MIN_CHARS) {
      report('pending')
      schedule()
    }
  }, [report, schedule])

  useEffect(() => {
    const onOnline = () => {
      if (stateRef.current === 'offline' || stateRef.current === 'error') void save()
    }
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [save])

  // Leaving with unsaved text: keep it on the device.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      if (versionRef.current > savedVersionRef.current && textRef.current.trim().length >= MIN_CHARS) {
        saveLocalDraft(ensureClientId(), {
          text: textRef.current,
          savedAt: Date.now(),
          context: contextRef.current,
          inputMethod: methodRef.current,
        })
      }
    },
    [ensureClientId],
  )

  return {
    text,
    setText,
    inputMethod,
    setInputMethod,
    captureId,
    state,
    recovered: start.recovered,
    flush,
  }
}
